import React, {useMemo} from 'react';
import {Box, Flex} from '@chakra-ui/react';
import {SelectableOptionsContainer} from './SelectableOptionsContainer';
import {
  AiEnrichmentAccountFilter,
  isMyAccountsFilter,
  isTaggedAccountsFilter,
  TaggedAccountsFilterSchema,
} from '../../../shared/enrichment';
import {zodTypeguard} from '../../../shared/api/helpers';
import {useTags} from '../../../hooks/api/tags';
import Select from 'react-select';
import _ from 'lodash';
import {z} from 'zod';

const AllAccountsFilterOption = ({
  accountFilter,
  onAccountFilterChange,
}: {
  accountFilter: AiEnrichmentAccountFilter | undefined;
  onAccountFilterChange: (accountFilter: AiEnrichmentAccountFilter) => void;
}) => {
  return (
    <SelectableOptionsContainer
      componentType="radio"
      title="All accounts in My Accounts"
      isSelected={isMyAccountsFilter(accountFilter)}
      onClick={() =>
        onAccountFilterChange({
          active: true,
          excluded: false,
        })
      }
    />
  );
};

const isEmptyTaggedAccountsFilter = zodTypeguard(
  TaggedAccountsFilterSchema.extend({
    tags: z.string().array().length(0),
  })
);

const AccountTagFilterOption = ({
  accountFilter,
  onAccountFilterChange,
}: {
  accountFilter: AiEnrichmentAccountFilter | undefined;
  onAccountFilterChange: (accountFilter: AiEnrichmentAccountFilter) => void;
}) => {
  const isSelected =
    isTaggedAccountsFilter(accountFilter) ||
    isEmptyTaggedAccountsFilter(accountFilter);
  const tags = useTags({});

  const options = useMemo(
    () =>
      tags.data?.map((tag) => ({
        label: tag.name,
        value: tag._id,
      })) ?? [],
    [tags.data]
  );

  return (
    <SelectableOptionsContainer
      componentType="radio"
      title="Only accounts with a specific tag"
      isSelected={isSelected}
      onClick={() => {
        // Ensure we don't remove a selected tag
        if (!isTaggedAccountsFilter(accountFilter)) {
          onAccountFilterChange({
            active: true,
            excluded: false,
            tags: [],
          });
        }
      }}
    >
      {isSelected && (
        <Box onClick={(e) => e.stopPropagation()}>
          <Select
            isLoading={tags.isLoading}
            isMulti={false}
            options={options}
            value={options.find((option) =>
              _.isEqual(option.value, accountFilter.tags[0])
            )}
            onChange={(option) => {
              if (!option) {
                return;
              }

              onAccountFilterChange({
                active: true,
                excluded: false,
                tags: [option.value],
              });
            }}
            placeholder="Select a tag"
          />
        </Box>
      )}
    </SelectableOptionsContainer>
  );
};

export const AccountSelectionOptions = ({
  accountFilter,
  onAccountFilterChange,
}: {
  accountFilter: AiEnrichmentAccountFilter | undefined;
  onAccountFilterChange: (accountFilter: AiEnrichmentAccountFilter) => void;
}) => {
  return (
    <Flex direction="column" gap="4">
      <AllAccountsFilterOption
        accountFilter={accountFilter}
        onAccountFilterChange={onAccountFilterChange}
      />
      <AccountTagFilterOption
        accountFilter={accountFilter}
        onAccountFilterChange={onAccountFilterChange}
      />
    </Flex>
  );
};
